import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Flex, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  FetchBlockItemDimensions,
  getAppliedFilters,
  getBlockDetails,
  getBlockDimensions,
  getIndicatorTableData,
  getSelectedBlockDimension,
  handleFilters,
  resetSelectedBlockDimension,
  setFilterItems,
  setMultiDimFilter,
  setSelectedBlockDimensionDirect,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch, getTableFilterPayload } from 'utils/GlobalHelpers';

import {
  buildDefaultDraft,
  countActiveFilterItems,
  selectionsFromRedux,
  selectionsToRedux,
} from '../planDimSelectionUtils';
import { PLAN_FILTER_BADGE_BG, PLAN_TEXT } from '../planPageRevampUi';
import { PlanToolbarChevronDownIcon, PlanToolbarFilterIcon } from '../icons';

import PlanDimSelectionModal, { type PlanDimensionOption } from './PlanDimSelectionModal';

import type { PlanDimDraftSelections } from '../planDimSelectionUtils';

interface Properties {
  onApplyFilters?: () => void;
}

const PlanFilterByControl = ({ onApplyFilters }: Properties) => {
  const { blockId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const blockDimensions = useSelector(getBlockDimensions);
  const selectedBlockDimension = useSelector(getSelectedBlockDimension);
  const appliedFilters = useSelector(getAppliedFilters);
  const blockDetails = useSelector(getBlockDetails);
  const indicatorTableData = useSelector(getIndicatorTableData);
  const baseScenario = useSelector(getBaseScenario);
  const themeColors = useSelector(getPlanPageThemeColors);

  const [isOpen, setIsOpen] = useState(false);
  const [activeDimensionName, setActiveDimensionName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [draftSelections, setDraftSelections] = useState<PlanDimDraftSelections>({});

  const dimensions = useMemo<PlanDimensionOption[]>(
    () =>
      (blockDimensions?.dimensions ?? []).map((dimension: any) => ({
        id: dimension.id,
        name: dimension.name,
        items: dimension.items ?? [],
      })),
    [blockDimensions?.dimensions],
  );

  const visibleDimensions = useMemo(
    () => dimensions.filter((dimension) => dimension.items.length > 0),
    [dimensions],
  );

  const activeFilterCount = useMemo(() => {
    const source =
      appliedFilters && appliedFilters.length > 0 ? appliedFilters : selectedBlockDimension;
    return countActiveFilterItems(source);
  }, [appliedFilters, selectedBlockDimension]);

  useEffect(() => {
    dispatch(
      FetchBlockItemDimensions({
        blockId,
        parameters: {
          params: { scenario_id: baseScenario?.id },
        },
      }),
    );
  }, [baseScenario?.id, blockId, dispatch]);

  useEffect(() => {
    if (blockDetails?.model_id && blockDetails?.id && blockDimensions?.dimensions?.length > 0) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        if (planFilters) {
          const data = JSON.parse(planFilters);
          const modalFilters = data[blockDetails.model_id]?.[blockDetails.id];
          if (modalFilters?.selectedBlockDimension) {
            dispatch(setSelectedBlockDimensionDirect(modalFilters.selectedBlockDimension));
          }
        }
      } catch {
        // Ignore invalid localStorage payload
      }
    }
  }, [blockDetails?.id, blockDetails?.model_id, blockDimensions?.dimensions, dispatch]);

  const persistFilters = useCallback(
    (selections: any[]) => {
      if (!blockDetails?.model_id || !blockDetails?.id) return;
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        const data: Record<string, any> = planFilters ? JSON.parse(planFilters) : {};
        const modelId = String(blockDetails.model_id);
        const currentBlockId = String(blockDetails.id);
        const modelData: Record<string, any> = data[modelId] || {};
        const blockFilters: any = modelData[currentBlockId] || {};
        blockFilters.selectedBlockDimension = selections;
        modelData[currentBlockId] = blockFilters;
        data[modelId] = modelData;
        localStorage.setItem('global-modal-filters', JSON.stringify(data));
      } catch {
        // Ignore localStorage write failures
      }
    },
    [blockDetails?.id, blockDetails?.model_id],
  );

  const applyIndicatorTableFilters = useCallback(() => {
    const activeDimIds = indicatorTableData?.dimensions?.map((item: any) => item.id);
    selectedBlockDimension.forEach((selection: any, index: number) => {
      const selectedValues = Object.values(selection)[0] as string[];
      const dimensionMeta = blockDimensions?.dimensions?.[index];
      if (
        selectedValues?.length > 0 &&
        dimensionMeta &&
        activeDimIds?.includes(String(dimensionMeta.id))
      ) {
        const payload = getTableFilterPayload(selection, dimensionMeta);
        dispatch(setFilterItems(payload.dimensionItems));
        dispatch(setMultiDimFilter(payload.tableItems));
      }
    });
  }, [
    blockDimensions?.dimensions,
    dispatch,
    indicatorTableData?.dimensions,
    selectedBlockDimension,
  ]);

  const openModal = useCallback(() => {
    const draft = selectionsFromRedux(selectedBlockDimension);
    const fallbackDimension = visibleDimensions[0]?.name ?? '';
    setDraftSelections(
      Object.keys(draft).length > 0 ? draft : buildDefaultDraft(visibleDimensions),
    );
    setActiveDimensionName(fallbackDimension);
    setSearchQuery('');
    setIsOpen(true);
  }, [selectedBlockDimension, visibleDimensions]);

  const handleApply = useCallback(() => {
    const payload = selectionsToRedux(draftSelections);
    dispatch(setSelectedBlockDimensionDirect(payload));
    dispatch(handleFilters());
    persistFilters(payload);
    applyIndicatorTableFilters();
    setIsOpen(false);
    onApplyFilters?.();
  }, [applyIndicatorTableFilters, dispatch, draftSelections, onApplyFilters, persistFilters]);

  const handleClearAll = useCallback(async () => {
    await dispatch(resetSelectedBlockDimension());
    const resetDraft = buildDefaultDraft(visibleDimensions);
    setDraftSelections(resetDraft);

    if (blockDetails?.model_id && blockDetails?.id) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        if (planFilters) {
          const data: Record<string, any> = JSON.parse(planFilters);
          const modelId = String(blockDetails.model_id);
          const currentBlockId = String(blockDetails.id);
          const modelData: Record<string, any> = data[modelId] || {};
          if (modelData[currentBlockId]) {
            delete modelData[currentBlockId].selectedBlockDimension;
            data[modelId] = modelData;
            localStorage.setItem('global-modal-filters', JSON.stringify(data));
          }
        }
      } catch {
        // Ignore localStorage clear failures
      }
    }

    setIsOpen(false);
    onApplyFilters?.();
  }, [blockDetails?.id, blockDetails?.model_id, dispatch, onApplyFilters, visibleDimensions]);

  const handleToggleItem = useCallback(
    (dimensionName: string, itemName: string, checked: boolean) => {
      setDraftSelections((previous) => {
        const currentItems = new Set(previous[dimensionName] ?? []);
        if (checked) {
          currentItems.add(itemName);
        } else {
          currentItems.delete(itemName);
        }
        return {
          ...previous,
          [dimensionName]: Array.from(currentItems),
        };
      });
    },
    [],
  );

  return (
    <>
      <Button
        size='sm'
        h='32px'
        minH='32px'
        px={0}
        py={0}
        w='fit-content'
        {...PLAN_TEXT.body}
        border='none'
        borderRadius='0'
        bg='transparent'
        color={themeColors.textDark}
        display='inline-flex'
        alignItems='center'
        flexWrap='nowrap'
        gap={3}
        lineHeight='1'
        boxShadow='none'
        _hover={{ bg: themeColors.activeBg }}
        _focus={{ boxShadow: 'none' }}
        onClick={openModal}
      >
        <PlanToolbarFilterIcon width={16} height={16} color={themeColors.primary} />
        <Text {...PLAN_TEXT.body} whiteSpace='nowrap' lineHeight='1.2'>
          Filter by
        </Text>
        {activeFilterCount > 0 && (
          <Flex
            minW='18px'
            h='18px'
            px={1}
            borderRadius='full'
            bg={PLAN_FILTER_BADGE_BG}
            color={themeColors.white}
            {...PLAN_TEXT.badge}
            alignItems='center'
            justifyContent='center'
            flexShrink={0}
          >
            {activeFilterCount}
          </Flex>
        )}
        <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
      </Button>

      <PlanDimSelectionModal
        isOpen={isOpen}
        dimensions={dimensions}
        activeDimensionName={activeDimensionName}
        draftSelections={draftSelections}
        searchQuery={searchQuery}
        onClose={() => setIsOpen(false)}
        onApply={handleApply}
        onClearAll={handleClearAll}
        onActiveDimensionChange={setActiveDimensionName}
        onSearchChange={setSearchQuery}
        onToggleItem={handleToggleItem}
      />
    </>
  );
};

export default memo(PlanFilterByControl);
